'use client';

import Link from 'next/link';
import { useState } from 'react';
import {
  BUSINESS_INFO,
  SERVICE_AREAS,
  SERVICE_AREAS_EXTENDED,
  SERVICE_AREAS_IMMEDIATE,
  SERVICE_AREAS_SECONDARY,
} from '@/lib/constants';

export function ServiceAreaMap() {
  const [mapLoaded, setMapLoaded] = useState(false);
  return (
    <section className="py-12 bg-white">
      <div className="container">
        <div className="text-center mb-8">
          <h2 className="text-3xl md:text-4xl font-bold dark-text">Our Service Area Coverage</h2>
          <p className="text-gray-600 mt-3 max-w-2xl mx-auto">
            We serve all communities within a 75-mile radius of Casper, WY. Fast response times and 
            professional service throughout the Casper region.
          </p>
        </div>

        <div className="max-w-5xl mx-auto">
          {/* Interactive Map (deferred load on user action) */}
          <div className="relative rounded-lg overflow-hidden shadow-lg mb-6">
            {!mapLoaded && (
              <div className="w-full h-[500px] bg-gray-200 flex items-center justify-center">
                <button
                  aria-label="Load interactive map"
                  onClick={() => setMapLoaded(true)}
                  className="px-4 py-2 bg-black text-white rounded-md shadow-md hover:bg-gray-800"
                >
                  Load Interactive Map
                </button>
              </div>
            )}
            {mapLoaded && (
              <iframe
                title="Service Area Map - Erroll Scheurer Plastering Coverage"
                src="https://www.google.com/maps/d/u/0/embed?mid=1d2Qa92whq5ircdctmERU-2i6LB9Zvxg&ehbc=2E312F&noprof=1"
                width="100%"
                height="500"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                className="w-full"
              />
            )}
            <div className="absolute bottom-4 left-4 bg-white rounded-lg shadow-lg p-4 max-w-xs">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-3 h-3 bg-blue-600 rounded-full" />
                <span className="text-sm font-semibold">Our Location</span>
              </div>
              <p className="text-xs text-gray-600">
                {BUSINESS_INFO.address.full}
              </p>
            </div>
          </div>

          {/* Service Radius Visual */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-green-50 border-2 border-green-200 rounded-lg p-6 text-center">
              <div className="w-16 h-16 mx-auto mb-3 bg-green-500 rounded-full flex items-center justify-center">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h3 className="font-bold text-lg mb-2">Immediate Area</h3>
              <p className="text-3xl font-bold text-green-600 mb-2">0-15 mi</p>
              <p className="text-sm text-gray-600">Same or next-day service</p>
              <p className="text-xs text-gray-700 mt-2">{SERVICE_AREAS_IMMEDIATE.length} communities</p>
            </div>

            <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-6 text-center">
              <div className="w-16 h-16 mx-auto mb-3 bg-blue-500 rounded-full flex items-center justify-center">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="font-bold text-lg mb-2">Secondary Area</h3>
              <p className="text-3xl font-bold text-blue-600 mb-2">15-35 mi</p>
              <p className="text-sm text-gray-600">Flexible scheduling</p>
              <p className="text-xs text-gray-700 mt-2">{SERVICE_AREAS_SECONDARY.length} communities</p>
            </div>

            <div className="bg-purple-50 border-2 border-purple-200 rounded-lg p-6 text-center">
              <div className="w-16 h-16 mx-auto mb-3 bg-purple-500 rounded-full flex items-center justify-center">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </div>
              <h3 className="font-bold text-lg mb-2">Extended Area</h3>
              <p className="text-3xl font-bold text-purple-600 mb-2">35-75 mi</p>
              <p className="text-sm text-gray-600">Larger projects welcome</p>
              <p className="text-xs text-gray-700 mt-2">{SERVICE_AREAS_EXTENDED.length} communities</p>
            </div>
          </div>

          {/* Coverage Stats */}
          <div className="mt-8 bg-gray-50 rounded-lg p-6">
            <div className="grid grid-cols-2 gap-4 text-center">
              <div>
                <p className="text-3xl font-bold text-[color:var(--color-primary)]">{SERVICE_AREAS.length}</p>
                <p className="text-sm text-gray-600">Cities Served</p>
              </div>
              <div>
                <p className="text-3xl font-bold text-[color:var(--color-primary)]">75</p>
                <p className="text-sm text-gray-600">Mile Radius</p>
              </div>
            </div>
          </div>

          <div className="mt-6 text-center">
            <p className="text-gray-600 mb-4">
              <strong>Don&apos;t see your city?</strong> We may still serve your area. Contact us to confirm.
            </p>
            <Link
              href="/service-areas"
              className="inline-flex items-center text-[color:var(--color-primary)] hover:underline font-medium"
            >
              View All Service Areas →
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
